import clsx from 'clsx';
import React from 'react';
import { InView } from 'react-intersection-observer';

import { AuthoredContentReactToHtmlParser } from '@utils/reactHtmlParserTransform';

import ConfidenceLevel from './ConfidenceLevel';
import PersonPhoto from './PersonPhoto';
import { InfluentialPersonProps, ProjectData, SAMPLE_DESCRIPTION } from './interface';

const toConfidenceLevel = (value?: number | string): 1 | 2 | 3 => {
  const parsed = Number(value);
  if (parsed >= 3) return 3;
  if (parsed >= 2) return 2;
  return 1;
};

const Description = ({ className, person }: { className: string; person: ProjectData }) => {
  if (person.description_preview) {
    return (
      <div className={className}>
        {person.description_preview.map((paragraph, index) => (
          <p
            className="mb-0 mt-0 [&:not(:first-child)]:mt-16"
            key={`${paragraph.sentence}-${index}`}
          >
            {paragraph.sentence}
            {paragraph.filler && (
              <span aria-hidden="true" className="select-none blur-[5px]">
                {' '}
                {paragraph.filler}
              </span>
            )}
          </p>
        ))}
      </div>
    );
  }

  const description = person.description?.trim() || SAMPLE_DESCRIPTION;

  return <p className={className}>{AuthoredContentReactToHtmlParser(description)}</p>;
};

const InfluentialPerson = ({ person }: InfluentialPersonProps) => {
  const confidenceLevel = toConfidenceLevel(person.debate_level);
  const subtitle = [person.title, person.company].filter(Boolean).join(', ');

  return (
    <InView threshold={0.3} triggerOnce>
      {({ inView, ref }) => (
        <article
          className={clsx(
            'min-w-0 max-w-full border-0 border-b border-solid border-primary-black py-32 md:py-48',
            '[contain-intrinsic-size:520px] [content-visibility:auto] sm:[contain-intrinsic-size:390px]',
            'transition-[opacity,transform] duration-700 ease-out',
            'motion-reduce:translate-y-0 motion-reduce:opacity-100 motion-reduce:transition-none',
            'md:flex md:items-start md:gap-40 md:even:flex-row-reverse',
            inView ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
          )}
          data-testid="influential-person"
          ref={ref}
        >
          <div className="flex gap-16 md:contents">
            <PersonPhoto
              className="h-[100px] w-[100px] md:h-[304px] md:w-[267px]"
              person={person}
            />
            <div className="min-w-0 flex-1">
              <h2 className="mb-0 mt-0 break-words font-suisse-screen text-disclosure-lg text-primary-black md:text-headline-lg">
                {person.name}
              </h2>
              {subtitle && (
                <p className="mb-0 mt-4 break-words font-suisse-screen text-disclosure-sm text-grayscale-charcoal md:mt-8 md:font-suisse-intl md:text-button-lg md:text-primary-black">
                  {subtitle}
                </p>
              )}
              <div className="mb-8 mt-8 border-0 border-b border-solid border-grayscale-beige md:hidden" />
              <div className="flex min-w-0 flex-wrap items-center gap-8 md:mt-8">
                <span className="min-w-0 font-suisse-screen text-disclosure-sm text-grayscale-charcoal">
                  Debate meter:
                </span>
                <ConfidenceLevel
                  animate={inView}
                  className="shrink-0"
                  confidenceLevel={confidenceLevel}
                />
              </div>
              <Description
                className="mb-0 mt-16 hidden break-words font-noto-serif text-body-articles text-primary-black md:block"
                person={person}
              />
            </div>
          </div>
          <Description
            className="mb-0 mt-16 break-words font-noto-serif text-body-articles text-primary-black md:hidden"
            person={person}
          />
        </article>
      )}
    </InView>
  );
};

export default InfluentialPerson;
